import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Table,
  CustomInput,
} from 'reactstrap';
import { AxiosAPI } from '../../../../../../helpers/AxiosAPI';

import {
  gAPI_Services_List_Bulk_Update,
  gAPI_Services_List_Translate,
} from '../../../../../../constants/APIs';

export default function AutoTranslateModal({
  isOpen,
  toggle,
  propertyLanguages,
  servicesList,
  languageCode,
  LoadData,
}) {
  const [sourceLanguage, setSourceLanguage] = useState('');
  const [targetLanguage, setTargetLanguage] = useState(languageCode || '');
  const [step, setStep] = useState(1);
  const [previewData, setPreviewData] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const getLocalText = (val) => {
    if (Array.isArray(val)) {
      const current = val.find((x) => x.languageCode === targetLanguage);
      if (current) return current.statement || '';
      const english = val.find((x) => x.languageCode === 'en');
      return english ? english.statement || '' : '';
    }
    return val || '';
  };

  const getPreviewRows = () => {
    return (servicesList || []).map((item) => ({
      id: item.id,
      title: getLocalText(item.title),
      highlights: getLocalText(item.displayText),
      instructions: getLocalText(item.instructions),
      buttonText: getLocalText(item.submitButtonText),
      translatedTitle: '',
      translatedHighlights: '',
      translatedInstructions: '',
      translatedButtonText: '',
    }));
  };

  const handlePreview = async () => {
    const rows = getPreviewRows();
    setPreviewData(rows);
    setSelectedItems([]);
    setStep(2);

    if (!gAPI_Services_List_Translate) {
      console.warn('Services preview translate API is not configured yet.');
      return;
    }
    setLoading(true);

    try {
      const payload = {
        sourceLang: sourceLanguage,
        targetLang: targetLanguage,
        data: rows.map((row) => ({
          id: row.id,
          title: row.title,
          highlights: row.highlights,
          buttonText: row.buttonText,
        })),
      };

      const response = await AxiosAPI({
        method: 'POST',
        url: gAPI_Services_List_Translate,
        data: payload,
      });

      const result = response.data;
      const translatedRows = rows.map((row) => {
        const translationItem = result?.translations?.find(
          (t) => t.original?.id == row.id
        );

        return {
          ...row,
          translatedTitle: translationItem?.translated?.title || row.title,
          translatedHighlights:
            translationItem?.translated?.highlights || row.highlights,
          translatedInstructions: row.instructions,
          translatedButtonText:
            translationItem?.translated?.buttonText || row.buttonText,
        };
      });

      setPreviewData(translatedRows);
    } catch (error) {
      console.error('Translation API error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleItem = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter((i) => i !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const handleToggleAll = () => {
    if (selectedItems.length === previewData.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(previewData.map((d) => d.id));
    }
  };

  const handleSave = async () => {
    if (!gAPI_Services_List_Bulk_Update) {
      console.warn('Services bulk update API is not configured yet.');
      return;
    }

    try {
      setSaveLoading(true);

      await AxiosAPI({
        method: 'PUT',
        url: gAPI_Services_List_Bulk_Update,
        data: {
          updates: selectedItems.map((id) => {
            const selectedRow = previewData.find((d) => d.id === id);
            return {
              id: selectedRow.id,
              title: {
                statement: selectedRow.translatedTitle,
                languageCode: targetLanguage,
              },
              displayText: {
                statement: selectedRow.translatedHighlights,
                languageCode: targetLanguage,
              },
              submitButtonText: {
                statement: selectedRow.translatedButtonText,
                languageCode: targetLanguage,
              },
            };
          }),
        },
      });

      LoadData(targetLanguage);
      toggle();
      setStep(1);
      setSourceLanguage('');
      setSelectedItems([]);
      setPreviewData([]);
    } catch (error) {
      console.error('Translation save error:', error);
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    if (languageCode) {
      setTargetLanguage(languageCode);
    }
  }, [languageCode]);

  const stickyHeaderStyle = {
    position: 'sticky',
    top: 0,
    background: '#fff',
    zIndex: 2,
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      contentClassName="d-flex flex-column"
      style={{ maxHeight: '85vh' }}
    >
      <ModalHeader toggle={toggle}>Auto Translate</ModalHeader>
      <ModalBody
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          maxHeight: '70vh',
        }}
      >
        {step === 1 && (
          <>
            <FormGroup>
              <Label>Select Source Language</Label>
              <Input
                type="select"
                value={sourceLanguage}
                onChange={(e) => setSourceLanguage(e.target.value)}
              >
                <option value="">Select a language</option>
                {propertyLanguages &&
                  propertyLanguages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name || lang.code}
                    </option>
                  ))}
              </Input>
            </FormGroup>

            <FormGroup>
              <Label>Select Target Language</Label>
              <Input
                type="select"
                value={targetLanguage === 'hk' ? 'zh-TW' : targetLanguage}
                onChange={(e) =>
                  setTargetLanguage(
                    e.target.value === 'zh-TW' ? 'hk' : e.target.value
                  )
                }
                disabled
              >
                <option value="">Select a language</option>
                {propertyLanguages &&
                  propertyLanguages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name || lang.code}
                    </option>
                  ))}
              </Input>
            </FormGroup>
          </>
        )}
        {step === 2 && (
          <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th style={{ ...stickyHeaderStyle, width: '50px' }}>
                    <CustomInput
                      type="checkbox"
                      id="selectAllTranslations"
                      label=""
                      checked={
                        selectedItems.length === previewData.length &&
                        previewData.length > 0
                      }
                      onChange={handleToggleAll}
                    />
                  </th>
                  <th style={stickyHeaderStyle}>Field</th>
                  <th style={stickyHeaderStyle}>Current Text</th>
                  <th style={stickyHeaderStyle}>Translated Text</th>
                </tr>
              </thead>
              <tbody>
                {previewData.map((data) => {
                  const topBorderOnly = { borderBottom: 'none' };
                  const noVerticalBorder = {
                    borderTop: 'none',
                    borderBottom: 'none',
                  };
                  const bottomBorderOnly = { borderTop: 'none' };

                  return (
                    <React.Fragment key={data.id}>
                      <tr>
                        <td
                          rowSpan="4"
                          style={{ verticalAlign: 'middle', width: '50px' }}
                        >
                          <CustomInput
                            type="checkbox"
                            id={`translate-item-${data.id}`}
                            label=""
                            checked={selectedItems.includes(data.id)}
                            onChange={() => handleToggleItem(data.id)}
                          />
                        </td>
                        <td
                          style={{
                            ...topBorderOnly,
                            whiteSpace: 'nowrap',
                            width: '140px',
                          }}
                        >
                          <strong>Title:</strong>
                        </td>
                        <td style={topBorderOnly}>{data.title}</td>
                        <td style={topBorderOnly}>
                          {loading ? (
                            <div
                              className="integration-text-skeleton-loader"
                              style={{
                                width: '80%',
                                height: '18px',
                                display: 'inline-block',
                              }}
                            ></div>
                          ) : (
                            data.translatedTitle
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{ ...noVerticalBorder, whiteSpace: 'nowrap' }}
                        >
                          <strong>Highlights:</strong>
                        </td>
                        <td
                          style={{
                            ...noVerticalBorder,
                            wordBreak: 'break-word',
                          }}
                        >
                          {data.highlights}
                        </td>
                        <td
                          style={{
                            ...noVerticalBorder,
                            wordBreak: 'break-word',
                          }}
                        >
                          {loading ? (
                            <div
                              className="integration-text-skeleton-loader"
                              style={{
                                width: '100%',
                                height: '18px',
                                display: 'inline-block',
                              }}
                            ></div>
                          ) : (
                            data.translatedHighlights
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{ ...noVerticalBorder, whiteSpace: 'nowrap' }}
                        >
                          <strong>Instructions:</strong>
                        </td>
                        <td
                          style={{
                            ...noVerticalBorder,
                            wordBreak: 'break-word',
                          }}
                        >
                          {data.instructions}
                        </td>
                        <td
                          style={{
                            ...noVerticalBorder,
                            wordBreak: 'break-word',
                          }}
                        >
                          {loading ? (
                            <div
                              className="integration-text-skeleton-loader"
                              style={{
                                width: '100%',
                                height: '18px',
                                display: 'inline-block',
                              }}
                            ></div>
                          ) : (
                            data.translatedInstructions
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{ ...bottomBorderOnly, whiteSpace: 'nowrap' }}
                        >
                          <strong>Button Text:</strong>
                        </td>
                        <td style={bottomBorderOnly}>{data.buttonText}</td>
                        <td style={bottomBorderOnly}>
                          {loading ? (
                            <div
                              className="integration-text-skeleton-loader"
                              style={{
                                width: '40%',
                                height: '18px',
                                display: 'inline-block',
                              }}
                            ></div>
                          ) : (
                            data.translatedButtonText
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {step === 1 ? (
          <Button
            color="primary"
            disabled={!sourceLanguage || loading}
            onClick={handlePreview}
          >
            {loading ? 'Translating...' : 'Preview Translation'}
          </Button>
        ) : (
          <Button
            color="primary"
            disabled={selectedItems.length === 0 || loading || saveLoading}
            onClick={handleSave}
          >
            {loading
              ? 'Translating...'
              : saveLoading
                ? 'Saving...'
                : 'Save Translation'}
          </Button>
        )}
        <Button
          color="secondary"
          onClick={() => {
            toggle();
            setStep(1);
            setSourceLanguage('');
          }}
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}
